import { defineStore } from 'pinia';
import { axios } from '@boot/axios.js';
import { posthog } from '@boot/posthog.js';
import { TYPES_OF_LOSSES } from '@models/causes.js';
import { useAuthStore } from '@stores/auth';
import Intercom from '@intercom/messenger-js-sdk';

export const useSettingsStore = defineStore('settings', {
    state: () => ({
        logoURL: null,
        typeOfLossOptions: [],
        clockDriftMs: 0,
        announcements: [],
        isTrackingQualityEvents: false,
        featureFlags: {},
        isIntercomInitialized: false,
        intercomJwtToken: null,
        intercomTokenFetchedAt: null
    }),

    getters: {
        /**
         * Retrieves the list of announcements that have not been dismissed by the user.
         * It filters through the state's `announcements` array and returns only those
         * where the `isDismissed` property is `false`.
         * @returns {Array} - A list of announcements that have not been dismissed (i.e., `isDismissed` is `false`).
         */
        getNonDismissedAnnouncements: state => {
            return state.announcements.filter(
                announcement => !announcement.isDismissed
            );
        },
        /**
         * Retrieves the value of a feature flag from the settings store.
         * @param {String} key - the feature flag key specified in Posthog (e.g. MY_FEATURE_FLAG_ENABLED)
         * @returns {Boolean} - value of the feature flag in Posthog, else false by default
         */
        getFeatureFlagValue: state => key => {
            return state.featureFlags[key] ?? false;
        }
    },

    actions: {
        /**
         * Resets the state to its default values.
         */
        resetState() {
            // Shutdown Intercom widget if initialized
            this.shutdownIntercom();

            this.$reset();
        },

        /**************
         * SETTINGS *
         *************/

        /**
         * Fetches the settings for the current client and updates the store.
         * @returns {Promise<void>}
         */
        async loadSettings() {
            try {
                const initDate = new Date();

                const response = await axios.get('clients/current/');

                const responseTimeMs = new Date() - initDate;
                console.debug(
                    'loadSettings response time (ms):',
                    responseTimeMs
                );

                const extractedSettings = validateAndExtractSettings(response);
                if (!extractedSettings) {
                    return;
                }
                const {
                    typeOfLossOptions,
                    logoURL,
                    isTrackingQualityEvents,
                    featureFlags
                } = extractedSettings;

                this.typeOfLossOptions = typeOfLossOptions;
                this.logoURL = logoURL;
                this.isTrackingQualityEvents = isTrackingQualityEvents;
                this.featureFlags = featureFlags;

                // check if the client's clock is out of sync with the server
                // if clockDriftMs is positive, the client's clock is behind
                // if clockDriftMs is negative, the client's clock is ahead
                const serverTime = new Date(
                    response.data?.current_utc_time || new Date().toISOString()
                );
                const clockDriftMs = serverTime - responseTimeMs - initDate;
                const clockDriftThresholdS =
                    response.data?.settings?.clock_drift_threshold_s || 5;
                if (Math.abs(clockDriftMs) > clockDriftThresholdS * 1000) {
                    console.warn(
                        'Client clock is out of sync with the server by',
                        clockDriftMs,
                        'ms'
                    );
                    this.clockDriftMs = clockDriftMs;

                    posthog.capture('has_clock_drift', {
                        drift_ms: clockDriftMs
                    });
                } else {
                    // ignore the drift if it is within the threshold
                    console.debug(
                        'Client clock is in sync with the server by',
                        clockDriftMs,
                        'ms'
                    );
                    this.clockDriftMs = 0;
                }

                const activeAnnouncements =
                    response.data?.active_announcements || [];
                if (
                    Array.isArray(activeAnnouncements) &&
                    activeAnnouncements.length
                ) {
                    activeAnnouncements.forEach(announcement => {
                        const formattedAnnouncement =
                            formatAnnouncement(announcement);

                        if (!formattedAnnouncement) {
                            console.error(
                                'Failed to format announcement:',
                                announcement
                            );
                        }

                        this.addAnnouncement(formattedAnnouncement);
                    });
                }
            } catch (error) {
                // in case of error, use the default type of loss options from models/causes.js
                this.typeOfLossOptions = TYPES_OF_LOSSES.map(
                    item => item.value
                );

                console.error('Failed to load settings:', error);
                throw new Error('Failed to load settings');
            }
        },

        /*****************
         * ANNOUNCEMENTS *
         *****************/

        /**
         * Adds an announcement received from SignalR to the Vuex store.
         * If an announcement with the same ID already exists, it will first be removed to ensure no duplicates.
         * @param {Object} announcement - The announcement object received from SignalR.
         */
        addAnnouncement(announcement) {
            const formattedAnnouncement = formatAnnouncement(announcement);
            if (!formattedAnnouncement) {
                console.error('Invalid announcement:', announcement);
                return; // Skip adding invalid announcements
            }
            // Proceed to add the valid announcement
            const filteredAnnouncements = this.announcements.filter(
                a => a.id !== formattedAnnouncement.id
            );
            this.announcements = [
                ...filteredAnnouncements,
                { ...formattedAnnouncement }
            ];
        },

        /**
         * Dismisses an announcement by marking it as dismissed.
         * @param {Number} announcementId - The unique identifier of the announcement to be dismissed.
         */
        dismissAnnouncement(announcementId) {
            const announcement = this.announcements.find(
                a => a.id === announcementId
            );
            if (announcement) {
                announcement.isDismissed = true;
            }
        },

        /*************
         * INTERCOM *
         ************/

        /**
         * Fetches the Intercom JWT token from the backend.
         * Token expires after 1 hour, so this should be called when needed.
         * @returns {Promise<string|null>} The JWT token or null if fetch fails
         * @private
         */
        async fetchIntercomToken() {
            try {
                const response = await axios.post('auth/intercom/token/');

                if (!response.data || !response.data.token) {
                    console.error(
                        'Invalid token response from server:',
                        response.data
                    );
                    return null;
                }

                const token = response.data.token;
                this.intercomJwtToken = token;
                this.intercomTokenFetchedAt = Date.now();

                return token;
            } catch (error) {
                console.error('Failed to fetch Intercom token:', error);
                return null;
            }
        },

        /**
         * Checks if the cached Intercom JWT token is still valid.
         * Tokens expire after 1 hour (3600000 ms).
         * @returns {boolean} True if token exists and is not expired
         * @private
         */
        isIntercomTokenValid() {
            if (!this.intercomJwtToken || !this.intercomTokenFetchedAt) {
                return false;
            }

            const TOKEN_EXPIRY_MS = 60 * 60 * 1000; // 1 hour
            const tokenAge = Date.now() - this.intercomTokenFetchedAt;

            return tokenAge < TOKEN_EXPIRY_MS;
        },

        /**
         * Initialize Intercom widget for customer support.
         * Retrieves user data from the auth store and initializes Intercom if all conditions are met.
         * Fetches a JWT token for secure identity verification.
         * Only initializes if:
         * - INTERCOM_WIDGET feature flag is enabled
         * - INTERCOM_APP_ID environment variable is set
         * - User is authenticated (retrieved from auth store)
         * - JWT token can be successfully fetched
         * - Not already initialized (prevents duplicate initialization)
         */
        async initializeIntercom() {
            // Check if feature flag is enabled
            if (!this.getFeatureFlagValue('INTERCOM_WIDGET')) {
                return;
            }

            // Check if app ID is configured
            const appId = process.env.INTERCOM_APP_ID;
            if (!appId) {
                console.warn('INTERCOM_APP_ID is not configured');
                return;
            }

            // Get user data from auth store
            const authStore = useAuthStore();
            const user = authStore.user;

            // Only initialize if user is authenticated
            if (!user || !user.id) {
                return;
            }

            // Don't reinitialize if already done
            if (this.isIntercomInitialized) {
                return;
            }

            try {
                // Fetch JWT token if not cached or expired
                let token = this.intercomJwtToken;
                if (!this.isIntercomTokenValid()) {
                    token = await this.fetchIntercomToken();
                }

                // Skip initialization if token fetch failed
                if (!token) {
                    console.error(
                        'Cannot initialize Intercom without valid JWT token'
                    );
                    return;
                }

                const fullName =
                    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                    '';

                Intercom({
                    app_id: appId,
                    intercom_user_jwt: token,
                    user_id: user.id,
                    name: fullName,
                    email: user.email
                });

                this.isIntercomInitialized = true;
            } catch (error) {
                console.error('Failed to initialize Intercom:', error);
            }
        },

        /**
         * Shutdown Intercom widget.
         * Called during store reset (see resetState action).
         */
        shutdownIntercom() {
            if (!this.isIntercomInitialized) {
                return;
            }

            try {
                if (typeof window !== 'undefined' && window.Intercom) {
                    window.Intercom('shutdown');
                }
            } catch (error) {
                console.error('Failed to shutdown Intercom:', error);
            }
        }
    }
});

/**
 * Formats an announcement object received from the server.
 * @param {Object} announcement - The announcement object received from the server.
 * @returns {Object} The formatted announcement object.
 */
export function formatAnnouncement(announcement) {
    if (!announcement || typeof announcement !== 'object') {
        console.error('Invalid announcement object:', announcement);
        return null;
    }

    if (
        !announcement.id ||
        !announcement.title ||
        !announcement.message ||
        !announcement.type
    ) {
        console.error('Invalid announcement properties:', announcement);
        return null;
    }

    return {
        id: announcement.id,
        title: announcement.title,
        message: announcement.message,
        type: announcement.type,
        // add isDismissed property so that we can
        // track which announcements have been dismissed
        isDismissed: false
    };
}

/**
 * Validates the API response and extracts settings properties.
 * @param {Object} response - The response object from the API.
 * @param {Object} response.data - The data property from the API response.
 * @returns {Object|undefined} Extracted settings properties or undefined if invalid.
 */
export function validateAndExtractSettings(response) {
    const responseValid = response.data && typeof response.data === 'object';
    if (!responseValid) {
        console.error('Invalid response from server:', response.data);
        return;
    }

    const settingsData =
        response.data.settings && typeof response.data.settings === 'object'
            ? response.data.settings
            : {};

    const formattedSettings = {
        typeOfLossOptions: settingsData?.type_of_loss_options || [],
        logoURL: settingsData?.logo_url || '',
        isTrackingQualityEvents: settingsData?.enable_quality_tracking || false,
        featureFlags: settingsData?.feature_flags || {}
    };

    // if type_of_loss_options is empty, use default values
    if (formattedSettings.typeOfLossOptions.length === 0) {
        formattedSettings.typeOfLossOptions = TYPES_OF_LOSSES.map(
            item => item.value
        );
    }

    return formattedSettings;
}
